import { memo, ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { ArrowUp } from 'lucide-react';
import CollectionSidebar from '@/features/collections/components/Sidebar';
import { useQuery } from '@tanstack/react-query';
import remoteDbService from '@/core/database/RemoteDBService';
import { DisplayedCollectionSection } from '@/features/collections/types/collection.types';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import { sortBy } from 'lodash-es';

type CollectionLayoutProps = {
  children: ReactNode;
  Menu?: ReactNode;
};

const SharedCollectionLayout = ({ children, Menu }: CollectionLayoutProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const setCurrentCollections = useCollectionStore(state => state.setCurrentCollection);
  const setCollectionStructure = useCollectionStore(state => state.setCollectionStructure);

  const { collectionId, pageId } = useParams<{ collectionId: string; pageId: string }>();

  const { data: collection } = useQuery({
    queryKey: [`collections/${collectionId as string}`],
    queryFn: async () => {
      const collection = await remoteDbService.getPublicCollectionById(collectionId as string);
      const notes = await remoteDbService.getCollectionNotes(collectionId as string);
      const sections = collection.sections || [];
      const structure: DisplayedCollectionSection[] = sections
        .filter(s => !s.archivedAt)
        .map(section => ({
          section: {
            ...section,
            pages: sortBy(
              notes.filter(p => p.sectionId === section.id),
              'orderNumber'
            ),
          },
        }));
      const enrichedCollection = {
        ...collection,
        structure,
      };
      return enrichedCollection;
    },
  });

  useEffect(() => {
    if (!collection) return;
    setCurrentCollections(collection);
    setCollectionStructure(collection.structure);
    const noteId = pageId || collection.structure[0]?.section.pages[0].id;
    navigate(`/shared/${collectionId as string}/page/${noteId}`);
  }, [collectionId, collection, navigate, pageId, setCollectionStructure, setCurrentCollections]);

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const container = containerRef.current;

    if (container) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollToTop();
        }, 100);
      });
    }
  }, [pageId, collectionId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setShowScrollButton(container.scrollTop > 400);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="flex h-screen">
      <CollectionSidebar
        onBackClick={() => {
          navigate('/shared');
        }}
      />
      <main ref={containerRef} className="flex-1 overflow-y-auto relative overflow-x-hidden">
        {Menu}
        {children}
        {showScrollButton && (
          <Button
            onClick={scrollToTop}
            className="fixed bottom-4 right-4 h-8 w-8 p-0 z-1000"
            variant="secondary"
            size="icon"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        )}
      </main>
    </div>
  );
};

export default memo(SharedCollectionLayout);
